/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import StoreShell, { useStoreParams } from "@/components/store/StoreShell";
import { fetchPublicCatalog } from "@/lib/api";

function money(minor) { return `₱${((minor || 0) / 100).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`; }

const AVAILABILITY_LABEL = { in_stock: null, low_stock: "Low stock", out_of_stock: "Out of stock" };

export default function StoreCatalog() {
  const { slug } = useStoreParams();
  const [catalog, setCatalog] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchPublicCatalog(slug, search ? { search } : {}).then(setCatalog).catch((err) => {
      if (err.response?.status === 404) setNotFound(true);
    });
  }, [slug, search]);

  if (notFound) return <Navigate to="/" replace />;

  return (
    <StoreShell slug={slug}>
      <h1 className="text-2xl font-bold font-display text-[#0F172A] mb-2">Shop</h1>
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products…"
        className="mb-6 w-full max-w-xs rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm" />

      {!catalog ? (
        <p className="text-sm text-[#475569]">Loading…</p>
      ) : catalog.items.length === 0 ? (
        <p className="text-sm text-[#94A3B8]">No products available yet.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="store-catalog-grid">
          {catalog.items.map((p) => (
            <Link key={p.id} to={`/store/${slug}/products/${p.id}`}
              className="rounded-2xl border border-[#E2E8F0] p-4 hover:border-[#10B981] transition-colors">
              {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="mb-3 h-40 w-full rounded-xl object-cover" />}
              <p className="font-medium text-[#0F172A]">{p.name}</p>
              <p className="mt-1 text-sm font-semibold text-[#0F172A]">{money(p.price_minor)}</p>
              {AVAILABILITY_LABEL[p.availability] && (
                <span className={`mt-1 inline-block text-xs font-medium ${p.availability === "out_of_stock" ? "text-red-600" : "text-amber-600"}`}>{AVAILABILITY_LABEL[p.availability]}</span>
              )}
            </Link>
          ))}
        </div>
      )}
    </StoreShell>
  );
}
